// src/warehouse/pos.js — раздел „Продажби от POS" в склада (ПРОМТ-13).
//
// Ресторантът продава през собствен софтуер, който не води склад, но дава
// таблица с продаденото за месеца. Тук: качване на таблицата (оригиналът се
// пази на сървъра), съпоставяне на колоните, съпоставяне на редовете с
// артикулите, преглед на изписването и потвърждаване.
//
// ПРОМТ-13б — първото пускане с по-малко ръчна работа: колоните се познават
// сами; редовете се делят на „За работа" и „Готови"; неразпознатите се
// решават наведнъж („Това са ястия" / „Не се изписват" / „Това е стока");
// артикулът се търси с писане, не в падащ списък; до „Потвърди" пише какво
// остава. Всяка промяна се записва веднага — няма „Запази" и няма как да се
// загуби работа.
//
// Правилата са на сървъра (routes/pos-sales.js, services/posSales.js):
// какво е ястие и какво стока, рецептите, себестойността, заключеният
// период. Браузърът прави само едно тежко нещо — прилага съпоставянето на
// колоните и СУМИРА редовете, за да не пътуват до сървъра хиляди касови
// бележки, а само по един ред на артикул.

import { el, clear, field, input, row } from '../shared/dom.js';
import { isoToBg, localTodayIso } from '../shared/dates.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { loadError, emptyState, searchInput } from '../shared/ui.js';
import { openModal, closeModal, confirmDialog } from '../shared/modal.js';
import { impReadFile, impParseBuffer, impNum, parseDateCell } from '../shared/importers.js';
import { uploadRaw } from '../shared/upload.js';
import { cq } from '../shared/state.js';
import { combobox } from '../shared/combobox.js';
import { xlsxDownload, withXlsx } from '../shared/xlsx.js';
import { plural, MONTHS_BG, amount, quantity } from '../shared/format.js';

const MAX_BYTES = 20 * 1024 * 1024;
const MAX_LINES = 5000;
const STOCK_TYPES = ['goods', 'material', 'dish'];

// Отвореният импорт; null = списъкът на месеците.
let _openId = null;
// Разчетените редове по импорт — за да не се чете файлът наново при всяко
// пречертаване. При отворено незавършено качване (след F5) се теглят от
// оригинала на сървъра.
const _rowsCache = {};
// „Съпостави колоните отново" — показва стъпката с колоните и при вече
// сумирани редове.
let _remap = false;

export function resetPos() { _openId = null; _remap = false; }

const f2 = amount;
const fq = quantity;
const STATUS = { mapping: ['за съпоставяне', 'amber'], confirmed: ['потвърден', 'green'], replaced: ['заменен', 'gray'] };
const statusTag = (s) => { const x = STATUS[s] || [s || '—', 'gray']; return el('span', { class: 'tag ' + x[1], text: x[0] }); };
const KIND = {
  goods: ['1:1', 'green'],
  dish: ['по рецептура', 'teal'],
  unmapped: ['неразпознат', 'red'],
  no_recipe: ['без рецептура', 'amber'],
  skip: ['не се изписва', 'gray'],
  ignore: ['не се води на склад', 'gray'],
};
const isProblem = (kind) => kind === 'unmapped' || kind === 'no_recipe';
const TYPE_BG = { goods: 'стока', material: 'суровина', dish: 'ястие' };
const SUGGESTED = { map: 'запомнено от предишен месец', code: 'познато по кода', name: 'познато по името' };

// ---- Съпоставяне на колоните (чисти функции, изнесени за теста) ----

export const MAP_FIELDS = [
  ['code', 'Код'],
  ['name', 'Наименование'],
  ['qty', 'Количество'],
  ['unit', 'Мярка'],
  ['date', 'Дата (по избор)'],
  ['price', 'Цена (по избор)'],
];

// Подсказките за познаване по заглавието — български и английски, както ги
// пишат касовите софтуери. Кратките („бр.", „id") се приемат само при точно
// съвпадение, иначе „id" би хванало всяка дума, която го съдържа.
const POS_HINTS = {
  code: ['код', 'арт №', 'арт. №', 'артикул №', 'номер на артикул', 'code', 'plu', 'sku', 'item code', 'product code', 'id'],
  name: ['наименование', 'име на артикул', 'артикул', 'продукт', 'стока', 'име', 'name', 'product', 'item', 'description', 'описание'],
  qty: ['количество', 'к-во', 'кол.', 'бр.', 'qty', 'quantity', 'count'],
  unit: ['мярка', 'мер. ед.', 'м. ед.', 'мерна единица', 'единица', 'unit', 'uom'],
  date: ['дата', 'date'],
  price: ['ед. цена', 'единична цена', 'цена', 'price', 'unit price'],
};

// Заглавието без интервали, тирета и точки и без значение главни/малки:
// I-CASH пише „Арт №" и „К - во" и при първия тест точно тези две колони
// останаха за ръчно избиране (ПРОМТ-13б).
const normHead = (s) => String(s == null ? '' : s).toLowerCase().replace(/[\s\-–—._]/g, '');

// cols → { поле: колона }. Първо точно съвпадение (след нормализиране), после
// „съдържа"; всяка колона отива най-много на едно поле, в реда на hints.
export function guessColumns(cols, hints) {
  const low = (s) => String(s || '').trim().toLowerCase();
  const out = {};
  const taken = new Set();
  const keys = Object.keys(hints);
  for (const pass of ['exact', 'contains']) {
    for (const k of keys) {
      if (out[k]) continue;
      for (const h of hints[k]) {
        const nh = normHead(h);
        if (!nh) continue;
        let test;
        if (pass === 'exact') test = (col) => normHead(col) === nh;
        // „Съдържа" е само за достатъчно дълги подсказки — буква по буква
        // (както преди) или след нормализиране.
        else if (h.length >= 4) test = (col) => low(col).includes(h) || (nh.length >= 4 && normHead(col).includes(nh));
        else continue;
        const c = cols.find((col) => !taken.has(col) && test(col));
        if (c) { out[k] = c; taken.add(c); break; }
      }
    }
  }
  return out;
}
export const guessMapping = (cols) => guessColumns(cols, POS_HINTS);

// „1 234,5" от Excel/CSV → 1234.5. impNum сам не маха интервалите.
const num = (v) => (typeof v === 'number' ? v : impNum(String(v == null ? '' : v).replace(/[\s ]/g, ''), 0));

// Прилага съпоставянето и сумира по (код, наименование, мярка) — сървърът
// очаква по един ред на артикул за месеца. Редове „Общо" и празни се
// прескачат; ред, който след сумирането е ≤ 0 (всичко върнато), отпада —
// складът приема само положителни количества.
export function applyMapping(rows, m, period) {
  const acc = new Map();
  let skipped = 0;
  let outside = 0;
  const val = (r, col) => (col ? String(r[col] == null ? '' : r[col]).trim() : '');
  for (const r of rows || []) {
    const code = val(r, m.code);
    const name = val(r, m.name);
    if (!code && !name) { skipped++; continue; }
    // Редът с общата сума обикновено е „Общо" в първата колона и празно в
    // другата — не е артикул.
    if ((!code || !name) && /^(общо|всичко|сума|total)/i.test(code || name)) { skipped++; continue; }
    const qty = num(m.qty ? r[m.qty] : 0);
    if (!qty) { skipped++; continue; }
    if (m.date && period) {
      const d = parseDateCell(r[m.date]);
      if (d && d.slice(0, 7) !== period) outside++;
    }
    const unit = val(r, m.unit);
    const key = [code.toLowerCase(), name.toLowerCase(), unit.toLowerCase()].join('\u0001');
    const cur = acc.get(key);
    if (cur) cur.qty += qty;
    else acc.set(key, { ext_code: code, ext_name: name, qty, unit });
  }
  const all = [...acc.values()].map((l) => Object.assign(l, { qty: Math.round(l.qty * 10000) / 10000 }));
  const lines = all.filter((l) => l.qty > 0);
  return { lines, skipped, dropped: all.length - lines.length, outside };
}

// ---- Списък на месеците ----

export async function posTab(area, ctx) {
  if (_openId) return saleDetail(area, ctx, _openId);
  let list;
  try { list = await api('/pos-sales' + cq()); }
  catch (e) { area.appendChild(loadError(() => { clear(area); posTab(area, ctx); }, e.message)); return; }
  if (!Array.isArray(list)) list = [];
  const panel = el('div', { class: 'panel' });
  panel.appendChild(el('div', { class: 'panel-head' }, el('h2', { text: 'Продажби от POS' }),
    el('div', { style: 'display:flex;gap:8px;flex-wrap:wrap' },
      el('button', { class: 'btn sm ghost', type: 'button', text: 'Свали макет (Excel)', onClick: () => downloadTemplate() }),
      el('button', { class: 'btn sm', type: 'button', text: 'Качи таблица за месец', onClick: () => uploadDialog(ctx, {}) }))));
  panel.appendChild(el('p', { class: 'cal-hint', text: 'Веднъж месечно: таблицата с продаденото от касовия софтуер → напитките и стоките се изписват 1:1, ястията — по рецептура, сумирано по материал, с дата последния ден на месеца.' }));
  if (!list.length) {
    panel.appendChild(emptyState('▥', 'Още няма внесени месеци. Натиснете „Качи таблица за месец".'));
    area.appendChild(panel);
    return;
  }
  const tb = el('tbody');
  for (const s of list) {
    const lbl = (s.period_label || s.period) + ' (' + (s.channel || 'POS') + ')';
    const acts = el('div', { class: 'row-actions' },
      el('button', { class: 'btn sm ghost', type: 'button', text: 'Отвори', 'aria-label': 'Отвори ' + lbl, onClick: () => { _openId = s.id; _remap = false; ctx.reload(); } }),
      s.status === 'confirmed' ? el('button', { class: 'btn sm ghost', type: 'button', text: 'Разписка', 'aria-label': 'Разходна разписка за ' + lbl, onClick: () => window.open('/api/pos-sales/' + s.id + '/pdf', '_blank') }) : null,
      s.file_id != null ? el('button', { class: 'btn sm ghost', type: 'button', text: 'Оригинал', 'aria-label': 'Оригиналният файл за ' + lbl, onClick: () => window.open('/api/pos-sales/' + s.id + '/file', '_blank') }) : null,
      s.status === 'confirmed' ? el('button', { class: 'btn sm ghost', type: 'button', text: 'Замени', 'aria-label': 'Замени ' + lbl, onClick: () => uploadDialog(ctx, { period: s.period, channel: s.channel, replace: true, label: s.period_label }) }) : null,
      s.status === 'mapping' ? el('button', { class: 'btn sm ghost', type: 'button', text: 'Изтрий', 'aria-label': 'Изтрий незавършеното качване ' + lbl, onClick: () => deleteSale(s, ctx) }) : null);
    tb.appendChild(el('tr', { class: s.status === 'replaced' ? 'row-inactive' : '' },
      el('td', { style: 'font-weight:600' }, s.period_label || s.period),
      el('td', {}, s.channel || ''),
      el('td', {}, statusTag(s.status)),
      el('td', { class: 'num' }, String(s.lines_count || 0)),
      el('td', {}, s.file_name || ''),
      el('td', {}, acts)));
  }
  panel.appendChild(el('table', { class: 'list' },
    el('thead', {}, el('tr', {}, el('th', { text: 'Месец' }), el('th', { text: 'Канал' }), el('th', { text: 'Статус' }),
      el('th', { class: 'num', text: 'Редове' }), el('th', { text: 'Файл' }), el('th', { text: '' }))),
    tb));
  area.appendChild(panel);
}

async function deleteSale(s, ctx) {
  const ok = await confirmDialog('Да изтрия ли незавършеното качване за ' + (s.period_label || s.period) + ' (' + s.channel + ')? В склада нищо не е изписано от него.', { okText: 'Изтрий', danger: true });
  if (!ok) return;
  try {
    await api('/pos-sales/' + s.id, { method: 'DELETE' });
    delete _rowsCache[s.id];
    toast('Качването е изтрито.');
    if (_openId === s.id) _openId = null;
    ctx.reload();
  } catch (e) { toast(e.message, true); }
}

// ---- Качване ----

// Изписването е след края на месеца — по подразбиране предлагаме миналия.
function prevMonth() {
  const [y, m] = localTodayIso().split('-').map(Number);
  return m === 1 ? (y - 1) + '-12' : y + '-' + String(m - 1).padStart(2, '0');
}

function uploadDialog(ctx, opts) {
  // Месецът се избира с думи („Август"), не се пише като „2026-08" (ПРОМТ-13б).
  const [py, pm] = String(opts.period || prevMonth()).split('-');
  const month = el('select', {}, ...MONTHS_BG.map((name, i) => {
    const v = String(i + 1).padStart(2, '0');
    return el('option', { value: v, text: name, selected: v === pm ? 'selected' : null });
  }));
  month.value = pm;
  const year = input({ type: 'number', min: '2020', max: '2100', step: '1', value: py, inputmode: 'numeric' });
  const channel = input({ value: opts.channel || 'POS', maxlength: '60' });
  if (opts.replace) { month.disabled = true; year.readOnly = true; channel.readOnly = true; }
  const pick = el('button', { class: 'btn', type: 'button', text: 'Избери файл и качи…', onClick: () => {
    const y = String(year.value || '').trim();
    const ch = String(channel.value || '').trim() || 'POS';
    if (!/^20\d{2}$/.test(y)) { toast('Въведете годината с четири цифри, напр. 2026.', true); year.focus(); return; }
    const period = y + '-' + month.value;
    impReadFile((rows, file) => {
      if (file && file.size > MAX_BYTES) { toast('Файлът е над 20 MB.', true); return; }
      doUpload(ctx, { period, channel: ch, rows, file, replace: !!opts.replace });
    }, { accept: '.xlsx,.xls,.csv' });
  } });
  const body = el('div', {},
    opts.replace
      ? el('p', { class: 'sev-warn', text: 'Замяна на ' + (opts.label || opts.period) + ': старите изписвания се сторнират едва когато потвърдите новия файл. Дотогава старият импорт остава в сила.' })
      : el('p', { text: 'Файл от касовия софтуер (Excel или CSV) с продаденото за месеца — ястия и напитки заедно. Оригиналът се пази.' }),
    opts.replace ? null : el('p', { class: 'cal-hint' }, 'Касата не дава таблица? ',
      el('button', { class: 'btn sm ghost', type: 'button', text: 'Свали макет (Excel)', onClick: () => downloadTemplate() }),
      ' — в него са всички ястия и стоки; попълнете колоната „Количество“ за месеца и го качете тук.'),
    row(field('Месец', month, { size: 'm' }), field('Година', year, { size: 's' }), field('Канал (касов софтуер)', channel, { size: 'm' })));
  openModal({ title: opts.replace ? 'Замяна на POS таблица' : 'Качи таблица за месец', body,
    foot: el('div', {}, el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal }), pick) });
}

// Макетът за изписването (Макс, 21.10.2026): готова таблица с всички ястия
// и стоки на фирмата и празна колона „Количество". Попълва се на ръка, когато
// касата не дава таблица (или за ръчен месец), и се качва като таблица от
// касата — заглавията са тези, които вносът познава сам (POS_HINTS).
// Суровините ги няма: те излизат по рецептурите, не се продават направо.
export function templateRows(articles) {
  const ORDER = { dish: 0, goods: 1 };
  return (Array.isArray(articles) ? articles : [])
    .filter((a) => a && a.active !== false && (a.type === 'dish' || a.type === 'goods'))
    .sort((a, b) => ORDER[a.type] - ORDER[b.type] || String(a.name || '').localeCompare(String(b.name || ''), 'bg'))
    .map((a) => ({ 'Код': String(a.code || ''), 'Наименование': a.name || '', 'Количество': '', 'Мярка': a.measure || '' }));
}

async function downloadTemplate() {
  let arts;
  try { arts = await api('/articles' + cq()); } catch (e) { toast(e.message, true); return; }
  const rows = templateRows(arts);
  if (!rows.length) { toast('Още няма ястия и стоки в „Артикули“ — първо ги добавете.', true); return; }
  xlsxDownload(rows, 'Продажби', 'Макет-изписване.xlsx');
  toast('Макетът е свален — ' + plural(rows.length, 'ред', 'реда') + '. Попълнете „Количество“ и го качете с „Качи таблица за месец“.');
}

async function doUpload(ctx, { period, channel, rows, file, replace }) {
  const url = '/api/pos-sales/upload' + cq() + '&channel=' + encodeURIComponent(channel) + '&period=' + encodeURIComponent(period) + (replace ? '&replace=1' : '');
  let sale;
  try {
    sale = await uploadRaw(url, file, { contentType: 'application/octet-stream' });
  } catch (e) {
    const d = e.data || {};
    if (e.status === 409 && d.exists != null && !replace) {
      // Сървърният текст вече пита („… вече е внесен за POS. Замяна?").
      const ok = await confirmDialog(e.message, { okText: 'Замени' });
      if (ok) return doUpload(ctx, { period, channel, rows, file, replace: true });
      return;
    }
    if (e.status === 409 && d.draft != null) {
      // Има започнато и незавършено качване за същия месец — отваряме него,
      // вместо да трупаме второ.
      toast(e.message, true);
      closeModal();
      _openId = d.draft;
      _remap = false;
      ctx.reload();
      return;
    }
    toast(e.message, true);
    return;
  }
  closeModal();
  _rowsCache[sale.id] = rows;
  _openId = sale.id;
  _remap = false;
  ctx.reload();
}

// Редовете на файла: от паметта или (след F5 / отворено по-късно) от
// оригинала на сървъра — не караме човека да търси файла втори път.
async function fileRows(sale) {
  if (_rowsCache[sale.id]) return _rowsCache[sale.id];
  let res;
  try { res = await fetch('/api/pos-sales/' + sale.id + '/file', { credentials: 'same-origin' }); }
  catch (e) { throw new Error('Няма връзка със сървъра — проверете интернета и опитайте пак.'); }
  if (!res.ok) throw new Error('Оригиналният файл не можа да се зареди.');
  const buf = await res.arrayBuffer();
  const rows = await withXlsx(() => impParseBuffer(buf, sale.file_name || ''));
  _rowsCache[sale.id] = rows;
  return rows;
}

// ---- Отворен импорт ----

async function saleDetail(area, ctx, id) {
  const back = el('button', { class: 'btn sm ghost', type: 'button', text: '← Към списъка', onClick: () => { resetPos(); ctx.reload(); } });
  let sale;
  try { sale = await api('/pos-sales/' + id); }
  catch (e) { area.appendChild(el('div', {}, back)); area.appendChild(loadError(() => { clear(area); saleDetail(area, ctx, id); }, e.message)); return; }
  const lbl = (sale.period_label || sale.period) + ' (' + (sale.channel || 'POS') + ')';
  // pos-panel: без отрязване по ръба — списъкът на търсачката в последния
  // ред трябва да излиза под панела, а лентата с действията да остава горе.
  const panel = el('div', { class: 'panel pos-panel' });
  panel.appendChild(el('div', { class: 'panel-head' },
    el('h2', {}, 'POS продажби — ' + lbl + ' ', statusTag(sale.status)),
    el('div', { class: 'row-actions' },
      back,
      sale.file_id != null ? el('button', { class: 'btn sm ghost', type: 'button', text: 'Оригинал', 'aria-label': 'Оригиналният файл за ' + lbl, onClick: () => window.open('/api/pos-sales/' + sale.id + '/file', '_blank') }) : null,
      sale.status === 'confirmed' ? el('button', { class: 'btn sm ghost', type: 'button', text: 'Разписка', 'aria-label': 'Разходна разписка за ' + lbl, onClick: () => window.open('/api/pos-sales/' + sale.id + '/pdf', '_blank') }) : null,
      sale.status === 'confirmed' ? el('button', { class: 'btn sm ghost', type: 'button', text: 'Замени', 'aria-label': 'Замени ' + lbl, onClick: () => uploadDialog(ctx, { period: sale.period, channel: sale.channel, replace: true, label: sale.period_label }) }) : null)));
  if (sale.replaces_id != null && sale.status === 'mapping') {
    panel.appendChild(el('p', { class: 'sev-warn', text: 'Това е замяна на вече внесен месец: при потвърждаване старите изписвания се сторнират и се записват новите.' }));
  }
  area.appendChild(panel);
  const lines = Array.isArray(sale.lines) ? sale.lines : [];
  if (sale.status === 'mapping' && (!lines.length || _remap)) return mappingStep(panel, sale, ctx);
  return linesStep(panel, sale, ctx);
}

// Стъпка 1: коя колона какво е. Запомня се по канал (на сървъра) — следващият
// месец идва попълнен.
async function mappingStep(panel, sale, ctx) {
  const box = el('div');
  panel.appendChild(box);
  let rows;
  try { rows = await fileRows(sale); }
  catch (e) { box.appendChild(loadError(() => { clear(box); box.remove(); mappingStep(panel, sale, ctx); }, e.message)); return; }
  if (!rows.length) { box.appendChild(emptyState('▥', 'Файлът е празен.')); return; }
  const cols = [];
  rows.slice(0, 50).forEach((r) => Object.keys(r).forEach((k) => { if (!cols.includes(k)) cols.push(k); }));
  let remembered = null;
  try { const r = await api('/pos-sales/mapping' + cq() + '&channel=' + encodeURIComponent(sale.channel || 'POS')); remembered = r && r.mapping; } catch (e) { remembered = null; }
  const guessed = guessMapping(cols);
  // Предимство: вече записаното за този импорт → запомненото за канала →
  // познатото по заглавията. Колона, която я няма във файла, не се предлага.
  const pickFor = (k) => [sale.mapping, remembered, guessed].map((m) => m && m[k]).find((c) => c && cols.includes(c)) || '';
  const sels = {};
  for (const [k] of MAP_FIELDS) {
    const cur = pickFor(k);
    sels[k] = el('select', { 'data-map': k },
      el('option', { value: '', text: '— няма такава колона —' }),
      ...cols.map((c) => el('option', { value: c, text: c, selected: c === cur })));
  }
  box.appendChild(el('h3', { text: 'Коя колона какво съдържа' }));
  const known = guessed.qty && (guessed.code || guessed.name);
  box.appendChild(el('p', { class: 'cal-hint', text: 'Файлът има ' + plural(rows.length, 'ред', 'реда') + '. '
    + (known ? 'Колоните са познати по заглавията — проверете ги и натиснете „Приложи и сумирай". ' : 'Посочете коя колона какво съдържа. ')
    + 'Изборът се запомня за „' + (sale.channel || 'POS') + '" — следващия месец идва попълнен.' }));
  box.appendChild(row(...MAP_FIELDS.slice(0, 3).map(([k, l]) => field(l, sels[k], { size: 'm' }))));
  box.appendChild(row(...MAP_FIELDS.slice(3).map(([k, l]) => field(l, sels[k], { size: 'm' }))));
  // Първите няколко реда — за да се види коя колона какво носи.
  const sample = rows.slice(0, 5);
  box.appendChild(el('div', { style: 'overflow-x:auto;margin:8px 0' }, el('table', { class: 'list', 'aria-label': 'Първите редове от файла' },
    el('thead', {}, el('tr', {}, ...cols.map((c) => el('th', { text: c })))),
    el('tbody', {}, ...sample.map((r) => el('tr', {}, ...cols.map((c) => el('td', { text: r[c] instanceof Date ? isoToBg(parseDateCell(r[c])) : String(r[c] == null ? '' : r[c]) }))))))));
  const go = el('button', { class: 'btn', type: 'button', text: 'Приложи и сумирай', onClick: async () => {
    const m = {};
    for (const [k] of MAP_FIELDS) m[k] = sels[k].value || '';
    if (!m.qty) { toast('Посочете колоната с количеството.', true); sels.qty.focus(); return; }
    if (!m.code && !m.name) { toast('Посочете поне колоната с кода или с наименованието.', true); sels.name.focus(); return; }
    const res = applyMapping(rows, m, sale.period);
    if (!res.lines.length) { toast('След съпоставянето не остана нито един ред с количество. Проверете колоните.', true); return; }
    if (res.lines.length > MAX_LINES) { toast('Над ' + MAX_LINES + ' различни артикула — проверете колоните (може да е избрана колона с номер на бележка).', true); return; }
    go.disabled = true;
    try {
      await api('/pos-sales/' + sale.id + '/rows', { method: 'PUT', body: JSON.stringify({ mapping: m, lines: res.lines }) });
      toast('Прочетени ' + plural(rows.length, 'ред', 'реда') + ' → ' + plural(res.lines.length, 'артикул', 'артикула') + ' след сумиране.');
      if (res.outside) toast(plural(res.outside, 'ред е', 'реда са') + ' с дата извън ' + (sale.period_label || sale.period) + ' — проверете дали файлът е за правилния месец.', true);
      if (res.dropped) toast(plural(res.dropped, 'артикул е', 'артикула са') + ' с общо количество 0 или по-малко (върнати) и не се изписват.', true);
      _remap = false;
      ctx.reload();
    } catch (e) { go.disabled = false; toast(e.message, true); }
  } });
  box.appendChild(el('div', { style: 'display:flex;gap:8px' }, go,
    _remap ? el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: () => { _remap = false; ctx.reload(); } }) : null));
}

// Стъпка 2: какво е всеки ред от касата — наш артикул, ястие или „не се
// изписва". Екранът показва първо само редовете ЗА РАБОТА; готовите са в
// съседния раздел. Всяко действие се записва веднага и сървърът връща целия
// импорт — оттам се пречертава всичко (видовете, броячите, какво остава).
async function linesStep(panel, sale0, ctx) {
  let sale = sale0;
  const editable = sale.status === 'mapping';
  // Списъкът с артикули се зарежда ВЕДНЪЖ за екрана; търсачките в редовете
  // го ползват наготово.
  let arts = [];
  const artLabel = (a) => (a.code ? a.code + ' ' : '') + (a.name || '');
  const artSub = (a) => (TYPE_BG[a.type] || '') + (a.measure ? ', ' + a.measure : '');
  const lbl = (sale.period_label || sale.period) + ' (' + (sale.channel || 'POS') + ')';
  const nameOf = (l, i) => l.ext_name || l.ext_code || ('ред ' + (i + 1));
  const lines = () => (Array.isArray(sale.lines) ? sale.lines : []);
  const isTodo = (l) => l.kind === 'unmapped';

  // Потвърден или заменен импорт няма „работа" — отваря се на всички редове.
  let view = editable ? 'todo' : 'all';
  let q = '';
  const selected = new Set();
  if (editable) await loadArticles();

  const viewsBox = el('div', { class: 'pos-views', role: 'group', 'aria-label': 'Кои редове да се виждат' });
  const tools = el('div', { style: 'display:flex;flex-wrap:wrap;gap:14px;align-items:center;margin:6px 0' },
    searchInput({ placeholder: 'Код или име от касата…', ariaLabel: 'Търсене в редовете от POS', onSearch: (v) => { q = v; draw(); } }));
  const bulk = el('div', { class: 'pos-bulk', role: 'group', 'aria-label': 'Какво са избраните редове' });
  const tb = el('tbody');
  const emptyNote = el('p', { class: 'cal-hint', role: 'status' });
  const leftBox = el('div', { class: 'pos-left' });
  const noRecipeBox = el('div');
  const planBox = el('div', { 'aria-live': 'polite' });

  // „Какво остава" и „Потвърди" са НАЙ-ГОРЕ: под 300 реда никой не ги вижда.
  if (editable) panel.appendChild(leftBox);
  panel.appendChild(viewsBox);
  panel.appendChild(tools);
  if (editable) panel.appendChild(bulk);
  panel.appendChild(el('table', { class: 'list pos-lines' },
    el('thead', {}, el('tr', {},
      editable ? el('th', { scope: 'col', style: 'width:30px' }, el('span', { class: 'sr-only', text: 'Избор' })) : null,
      el('th', { text: 'Код (каса)' }), el('th', { text: 'Наименование (каса)' }), el('th', { class: 'num', text: 'Количество' }),
      el('th', { text: 'Мярка' }), el('th', { text: 'Какво е в склада' }), el('th', { text: 'Изписване' }))),
    tb));
  panel.appendChild(emptyNote);
  if (editable) panel.appendChild(noRecipeBox);
  panel.appendChild(planBox);

  // Сървърът върна новото състояние на импорта.
  function apply(next) {
    if (!next || !Array.isArray(next.lines)) return;
    sale = next;
    // Изборът важи само за редове, които още чакат решение.
    for (const i of [...selected]) if (!sale.lines[i] || !isTodo(sale.lines[i])) selected.delete(i);
    clear(planBox);
    draw();
  }
  async function send(path, method, body) {
    try {
      const r = await api(path, { method, body: JSON.stringify(body) });
      apply(r);
      return r;
    } catch (e) { toast(e.message, true); return null; }
  }
  const setLine = (i, patch) => send('/pos-sales/' + sale.id + '/lines', 'PUT', { lines: [Object.assign({ index: i }, patch)] });
  async function loadArticles() {
    let list = [];
    try { list = await api('/articles' + cq()); } catch (e) { return; }
    arts = (Array.isArray(list) ? list : []).filter((a) => STOCK_TYPES.includes(a.type) && a.active !== false)
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'bg'));
  }
  async function classify(body) {
    const r = await send('/pos-sales/' + sale.id + '/classify', 'POST', body);
    // Новите ястия и стоки трябва да се намират в търсачките веднага.
    if (r && r.created) await loadArticles();
    return r;
  }

  function kindTag(k) {
    const x = KIND[k] || [k || '—', 'gray'];
    return el('span', { class: 'tag ' + x[1], text: x[0] });
  }

  function artCell(l, i) {
    const nm = nameOf(l, i);
    if (!editable) return el('td', {}, l.ignore ? 'не се изписва' : (l.article_id != null ? ((l.article_code ? l.article_code + ' ' : '') + (l.article_name || '')) : '—'));
    if (l.ignore) {
      return el('td', {}, el('span', { text: 'Не се изписва от склада ' }),
        el('button', { class: 'btn sm ghost', type: 'button', text: 'Върни', 'aria-label': 'Върни за работа, ' + nm, onClick: () => setLine(i, { article_id: null, ignore: false }) }));
    }
    const notes = [];
    if (l.article_id == null && l.hint === 'material_name') notes.push('Има суровина със същото име — това вероятно е ястие.');
    if (l.article_id != null && l.article_type === 'material') notes.push('Изписва се 1:1 от суровината.');
    else if (l.article_id != null && SUGGESTED[l.suggested]) notes.push(SUGGESTED[l.suggested]);
    const box = combobox({
      items: () => arts, label: artLabel, sub: artSub, value: l.article_id,
      ariaLabel: 'Артикул в склада, ' + nm, placeholder: 'Търси по име или код…',
      emptyText: 'Няма такъв артикул. Ако е ястие — отметнете реда и натиснете „Това са ястия".',
      onPick: (a) => setLine(i, { article_id: a ? a.id : null }),
    });
    return el('td', {}, box, ...notes.map((t) => el('div', { class: 'cal-hint', style: 'padding:2px 0', text: t })));
  }

  function rowOf(l, i) {
    const nm = nameOf(l, i);
    const cls = isProblem(l.kind) ? 'stock-low' : (l.article_type === 'material' && l.article_id != null ? 'pos-material' : '');
    return el('tr', { class: cls },
      editable ? el('td', {}, el('input', { type: 'checkbox', checked: selected.has(i), 'aria-label': 'Избери ' + nm,
        onChange: (e) => { if (e.target.checked) selected.add(i); else selected.delete(i); drawBulk(); } })) : null,
      el('td', { style: 'font-variant-numeric:tabular-nums' }, l.ext_code || ''),
      el('td', { style: 'font-weight:600' }, l.ext_name || ''),
      el('td', { class: 'num' }, fq(l.qty)),
      el('td', {}, l.unit || ''),
      artCell(l, i),
      el('td', {}, kindTag(l.kind)));
  }

  function drawViews() {
    clear(viewsBox);
    const all = lines();
    const todo = all.filter(isTodo).length;
    const btn = (id, text, n) => el('button', {
      class: 'pos-view', type: 'button', 'aria-pressed': view === id ? 'true' : 'false', text: text + ' (' + n + ')',
      onClick: () => { view = id; draw(); },
    });
    viewsBox.appendChild(btn('todo', 'За работа', todo));
    viewsBox.appendChild(btn('done', 'Готови', all.length - todo));
    viewsBox.appendChild(btn('all', 'Всички', all.length));
  }

  function drawBulk() {
    if (!editable) return;
    clear(bulk);
    // Само видимите: при търсене „кутия" не се отметват и скритите редове.
    const todoIdx = lines().map((l, i) => (isTodo(l) && matchesQ(l) ? i : -1)).filter((i) => i >= 0);
    const n = selected.size;
    bulk.appendChild(el('button', { class: 'btn sm ghost', type: 'button', text: q ? 'Избери намерените неразпознати' : 'Избери всички неразпознати', disabled: todoIdx.length ? null : 'disabled',
      onClick: () => { todoIdx.forEach((i) => selected.add(i)); draw(); } }));
    if (n) bulk.appendChild(el('button', { class: 'btn sm ghost', type: 'button', text: 'Изчисти избора', onClick: () => { selected.clear(); draw(); } }));
    bulk.appendChild(el('span', { class: 'pos-bulk-count', role: 'status', text: n ? 'Избрани: ' + plural(n, 'ред', 'реда') + ' →' : 'Отметнете редове и кажете какво са:' }));
    const off = n ? null : 'disabled';
    bulk.appendChild(el('button', { class: 'btn sm', type: 'button', text: 'Това са ястия', disabled: off, onClick: async () => {
      const idx = [...selected];
      const ok = await confirmDialog('Да отбележа ли ' + plural(idx.length, 'ред', 'реда') + ' като ястия? За всеки ред, който още няма ястие със същото име, ще се създаде артикул „ястие" с името от касата. После им трябват рецептури.', { okText: 'Това са ястия' });
      if (!ok) return;
      const r = await classify({ action: 'dish', indexes: idx });
      if (r) toast(plural(idx.length, 'ред е отбелязан', 'реда са отбелязани') + ' като ястия' + (r.created ? ' — ' + plural(r.created, 'ново ястие', 'нови ястия') + ' в „Артикули"' : '') + '.');
    } }));
    bulk.appendChild(el('button', { class: 'btn sm ghost', type: 'button', text: 'Не се изписват', disabled: off, onClick: async () => {
      const idx = [...selected];
      const ok = await confirmDialog(plural(idx.length, 'ред', 'реда') + ' няма да се изписват от склада — и в следващите месеци. Ред се връща с „Върни".', { okText: 'Не се изписват' });
      if (!ok) return;
      const r = await classify({ action: 'skip', indexes: idx });
      if (r) toast(plural(idx.length, 'ред няма', 'реда няма') + ' да се изписва от склада. Запомнено е и за следващите месеци.');
    } }));
    bulk.appendChild(el('button', { class: 'btn sm ghost', type: 'button', text: 'Това е стока', disabled: n === 1 ? null : 'disabled',
      title: n === 1 ? null : 'Стока се избира ред по ред — отметнете само един ред.',
      onClick: () => { const i = [...selected][0]; if (i != null) goodsDialog(i); } }));
  }

  // „Това е стока" — само за един ред: съществуваща стока от търсачката или
  // „+ Нова стока" с името от касата. Масово създаване няма нарочно — иначе
  // излизат двойници на стоките от Конто с малко различно име.
  function goodsDialog(i) {
    const l = lines()[i];
    if (!l) return;
    const nm = nameOf(l, i);
    const box = combobox({
      items: () => arts.filter((a) => a.type === 'goods'), label: artLabel, sub: artSub, inline: true,
      placeholder: 'Търси стока по име или код…',
      emptyText: 'Няма такава стока — най-долу е „+ Нова стока".',
      onPick: async (a) => {
        if (!a) return;
        closeModal();
        const r = await classify({ action: 'goods', indexes: [i], article_id: a.id });
        if (r) toast('„' + nm + '" е „' + a.name + '" — запомнено за следващите месеци.');
      },
      footer: { label: () => '+ Нова стока „' + nm + '"', onPick: async () => {
        closeModal();
        const r = await classify({ action: 'goods', indexes: [i], create: true });
        if (r) toast('Създадена е стока „' + nm + '" в „Артикули".');
      } },
    });
    openModal({ title: 'Коя стока е „' + nm + '"?', dirtyCheck: () => false,
      body: el('div', {},
        el('p', { text: 'Изберете стоката от склада, която касата продава под това име. Ако я няма в списъка — „+ Нова стока" най-долу.' }),
        field('Стока в склада', box)),
      foot: el('div', {}, el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal })) });
    // Списъкът е отворен веднага — „+ Нова стока" да се вижда без писане.
    box.open();
  }

  function matchesQ(l) {
    return !q || [l.ext_code, l.ext_name, l.article_name].some((v) => String(v || '').toLowerCase().includes(q));
  }
  function drawRows() {
    clear(tb);
    let shown = 0;
    lines().forEach((l, i) => {
      if (view === 'todo' && !isTodo(l)) return;
      if (view === 'done' && isTodo(l)) return;
      if (!matchesQ(l)) return;
      tb.appendChild(rowOf(l, i));
      shown++;
    });
    emptyNote.textContent = shown ? '' : (q ? 'Нищо не отговаря на търсенето.'
      : view === 'todo' ? 'Няма редове за работа — всичко от касата е разпознато.' : 'Тук още няма редове.');
    emptyNote.style.display = shown ? 'none' : '';
  }

  const left = () => sale.left || { unmapped: 0, no_recipe: [] };
  function leftText() {
    const u = left().unmapped;
    const d = left().no_recipe.length;
    if (!u && !d) return 'Всичко е готово — можете да потвърдите.';
    const parts = [u ? plural(u, 'ред', 'реда') : null, d ? plural(d, 'ястие', 'ястия') + ' без рецептура' : null].filter(Boolean);
    return (u + d === 1 ? 'Остава ' : 'Остават ') + parts.join(' и ') + '.';
  }

  async function fetchPlan() {
    try { return await api('/pos-sales/' + sale.id + '/plan'); }
    catch (e) { toast(e.message, true); return null; }
  }

  function drawLeft() {
    if (!editable) return;
    clear(leftBox);
    clear(noRecipeBox);
    const remaining = left().unmapped + left().no_recipe.length;
    const confirmBtn = el('button', { class: 'btn', type: 'button', text: 'Потвърди', disabled: remaining ? 'disabled' : null, onClick: async () => {
      if (remaining) return;
      const plan = await fetchPlan();
      if (!plan) return;
      // Мярка, която не пасва, или недостиг при „Забранявай" се виждат едва в
      // плана — тогава показваме прегледа, вместо да питаме за потвърждение.
      if (!plan.valid || plan.error || plan.lock_error) { renderPlan(planBox, plan, ctx); return; }
      const ok = await confirmDialog('Да изпиша ли от склада продажбите за ' + lbl + '? Изписването не се редактира — поправка става със замяна (сторно + нов файл).', { okText: 'Потвърди' });
      if (!ok) return;
      try {
        const r = await api('/pos-sales/' + sale.id + '/confirm', { method: 'POST', body: JSON.stringify({}) });
        toast('Изписването за ' + lbl + ' е потвърдено.');
        if (r && Array.isArray(r.warnings) && r.warnings.length) toast('Внимание: ' + warnText(r.warnings), true);
        ctx.reload();
      } catch (e) { toast(e.message, true); }
    } });
    leftBox.appendChild(confirmBtn);
    leftBox.appendChild(el('span', { class: 'pos-left-text', role: 'status', text: leftText() }));
    leftBox.appendChild(el('button', { class: 'btn ghost', type: 'button', text: 'Преглед на изписването', onClick: async () => {
      const plan = await fetchPlan();
      if (plan) renderPlan(planBox, plan, ctx);
    } }));
    leftBox.appendChild(el('button', { class: 'btn ghost', type: 'button', text: 'Съпостави колоните отново', onClick: () => { _remap = true; ctx.reload(); } }));

    const dishes = left().no_recipe;
    if (dishes.length) {
      noRecipeBox.appendChild(el('h3', { text: 'Ястия без рецептура (' + dishes.length + ')' }));
      noRecipeBox.appendChild(el('p', { class: 'cal-hint', text: 'Докато ястие няма рецептура, месецът не може да се потвърди. Въведете ги една по една оттук или всички наведнъж от Excel.' }));
      noRecipeBox.appendChild(el('div', {}, el('button', { class: 'btn sm ghost', type: 'button', text: 'Внос на рецептури от Excel', onClick: () => ctx.openRecipe(null) })));
      noRecipeBox.appendChild(el('ul', { class: 'pos-norecipe', style: 'max-height:260px;overflow:auto' },
        ...dishes.map((d) => el('li', {}, (d.name || ('Артикул ' + d.article_id)) + ' ',
          el('button', { class: 'btn sm ghost', type: 'button', text: 'Направи рецептура', 'aria-label': 'Направи рецептура за ' + (d.name || ''), onClick: () => ctx.openRecipe(d.article_id) })))));
    }
  }

  function draw() {
    drawViews();
    drawBulk();
    drawRows();
    drawLeft();
  }
  draw();
}

// Предупрежденията за недостиг идват по едно на артикул. До три се казват
// направо; при повече се казва само броят и къде се виждат — при първия
// месец без начални салда те са десетки и съобщението покриваше екрана.
function warnText(list) {
  const w = Array.isArray(list) ? list : [];
  if (w.length <= 3) return w.join(' ');
  return plural(w.length, 'артикул е', 'артикула са') + ' с недостиг — наличността им става отрицателна. Вижте „Наличности" → „Отрицателни".';
}

function namesList(names, max) {
  const list = (names || []).slice(0, max);
  const more = (names || []).length - list.length;
  return list.join(', ') + (more > 0 ? ' и още ' + more : '');
}

function renderPlan(box, plan, ctx) {
  clear(box);
  const s = plan.summary || {};
  const p = plan.problems || {};
  box.appendChild(el('h3', { text: 'Преглед на изписването' }));
  box.appendChild(el('p', { class: 'pos-plan-summary', text:
    plural(s.n || 0, 'ред', 'реда') + '; ' + (s.goods || 0) + ' напитки/стоки → 1:1; ' + (s.dishes || 0) + ' ястия → по рецептура; '
    + (s.no_recipe || 0) + ' без рецептура (спират)'
    + (s.unmapped ? '; ' + s.unmapped + ' неразпознати (спират)' : '')
    + (s.skipped ? '; ' + s.skipped + ' не се изписват' : '')
    + (s.ignored ? '; ' + s.ignored + ' не се водят на склад' : '') + '.' }));
  if (!plan.valid) {
    const ul = el('ul', { style: 'margin:4px 0 0;padding-left:20px' });
    const dishes = Array.isArray(plan.no_recipe_dishes) ? plan.no_recipe_dishes : [];
    if (dishes.length) {
      ul.appendChild(el('li', {}, plural(dishes.length, 'ястие е', 'ястия са') + ' без рецептура: ' + namesList(dishes.map((d) => d.name), 20) + ' ',
        el('button', { class: 'btn sm ghost', type: 'button', text: 'Към рецептурите', onClick: () => (ctx.openRecipe ? ctx.openRecipe(null) : ctx.goTab('recipes')) })));
    } else if (p.no_recipe && p.no_recipe.length) {
      ul.appendChild(el('li', { text: 'Без рецептура: ' + namesList(p.no_recipe, 20) }));
    }
    if (p.unmapped && p.unmapped.length) ul.appendChild(el('li', { text: plural(p.unmapped.length, 'ред не е разпознат', 'реда не са разпознати') + ': ' + namesList(p.unmapped, 20) }));
    (p.errors || []).forEach((t) => ul.appendChild(el('li', { text: t })));
    box.appendChild(el('div', { class: 'sev-warn pos-plan-problems', role: 'alert' },
      el('strong', { text: 'Потвърждаването е спряно. ' }), 'Ето какво пречи:', ul));
  }
  if (plan.opening_after) {
    box.appendChild(el('div', { class: 'sev-warn pos-plan-opening', role: 'alert' },
      el('strong', { text: 'Началното салдо е с дата ' + isoToBg(plan.opening_after.date) + ' — след изписването (' + isoToBg(plan.date) + '). ' }),
      'Затова към ' + isoToBg(plan.date) + ' в склада още няма нищо и излиза недостиг с цени 0,00. Администраторът премества датата на началното салдо в „Склад“ → „Ръчно движение“ → „Дата на началното салдо“ (напр. на последния ден на предходния месец).'));
  }
  if (plan.lock_error) box.appendChild(el('div', { class: 'sev-warn', role: 'alert', text: plan.lock_error }));
  if (plan.error) box.appendChild(el('div', { class: 'sev-warn', role: 'alert', text: plan.error }));
  const issues = plan.issues || [];
  if (issues.length) {
    const tb = el('tbody', {}, ...issues.map((r) => el('tr', {},
      el('td', {}, r.code || ''), el('td', {}, r.name || ''), el('td', {}, r.measure || ''),
      el('td', { class: 'num' }, fq(r.qty)), el('td', { class: 'num' }, r.unit_cost == null ? '—' : f2(r.unit_cost)),
      el('td', { class: 'num' }, r.total == null ? '—' : f2(r.total)))));
    box.appendChild(el('p', { class: 'cal-hint', text: 'Изписване по материали с дата ' + isoToBg(plan.date) + '. Цените са средните от склада към тази дата — не от рецептурата.' }));
    box.appendChild(el('table', { class: 'list' },
      el('thead', {}, el('tr', {}, el('th', { text: 'Код' }), el('th', { text: 'Материал / стока' }), el('th', { text: 'Мярка' }),
        el('th', { class: 'num', text: 'Количество' }), el('th', { class: 'num', text: 'Ед. цена' }), el('th', { class: 'num', text: 'Стойност' }))),
      tb,
      el('tfoot', {}, el('tr', { style: 'font-weight:700' }, el('td', { colspan: '5', text: 'Общо' }), el('td', { class: 'num', text: f2(plan.total) })))));
  }
  if (Array.isArray(plan.warnings) && plan.warnings.length) {
    const many = plan.warnings.length > 3;
    box.appendChild(el('div', { class: 'sev-warn pos-plan-warnings', role: 'status' }, el('strong', { text: 'Внимание: ' }), warnText(plan.warnings),
      many ? el('details', {}, el('summary', { text: 'Покажи всички' }),
        el('ul', { style: 'margin:4px 0 0;padding-left:20px;max-height:220px;overflow:auto' }, ...plan.warnings.map((t) => el('li', { text: t })))) : null));
  }
}
